import { BigNumber } from '@0x/utils'
import assetUtils, { Asset } from 'app-lib/assetUtils'
import { chainConfigs } from '../../config/appConfig'
import providerUtils, { AnyProvider } from '../providerUtils'

// Returns swap rate from Chainlink Oracle
/** Note that the rate is divided by the precision difference of both token precision */
async function getSwapRateNew(
  provider: AnyProvider,
  srcAsset: Asset,
  destAsset: Asset
): Promise<BigNumber> {
  const { contractsSource, chainId } = providerUtils.getDependenciesStrictRead(provider)
  const srcAddress = assetUtils.getAddress(chainId, srcAsset)
  const destAddress = assetUtils.getAddress(chainId, destAsset)
  const oracleContract = contractsSource.getOracleContract()

  const [rate, precision] = await oracleContract.queryRate(srcAddress, destAddress).callAsync()
  return rate.div(precision)
}

// Returns swap rate from Chainlink Oracle
async function getSwapRate(
  provider: AnyProvider,
  srcAsset: Asset,
  destAsset: Asset
): Promise<BigNumber> {
  const { contractsSource, chainId } = providerUtils.getDependenciesStrictRead(provider)
  let result: BigNumber = new BigNumber(0)
  const [srcToken, destToken] = assetUtils.getTokensInfo(chainId, [srcAsset, destAsset])
  const oracleContract = contractsSource.getOracleContract()

  const swapPriceData: BigNumber[] = await oracleContract
    .queryRate(srcToken.address, destToken.address)
    .callAsync()

  result = swapPriceData[0]
    .times(10 ** (18 - srcToken.decimals))
    .div(10 ** (18 - destToken.decimals))
    .dividedBy(10 ** 18)
    .multipliedBy(swapPriceData[1].dividedBy(10 ** 18)) // swapPriceData[0].dividedBy(10 ** 18);

  return result
}

function getGoodSourceAmountOfAsset(asset: Asset): BigNumber {
  switch (asset) {
    case Asset.WBTC:
      return new BigNumber(10 ** 6)
    case Asset.USDC:
    case Asset.USDT:
      return new BigNumber(10 ** 4)
    default:
      return new BigNumber(10 ** 16)
  }
}

async function getSwapToUsdRateBatch(
  provider: AnyProvider,
  assets: Asset[],
  usdToken: Asset
): Promise<[BigNumber[], BigNumber[], BigNumber[]]> {
  const { helperContract } = providerUtils.getDependenciesStrictRead(provider)
  let result: [BigNumber[], BigNumber[], BigNumber[]] = [[], [], []]

  const usdTokenAddress = assetUtils.getAddress(provider.currentChainId, usdToken)
  const underlyings = assets.map((e) =>
    assetUtils.getAddress(provider.currentChainId, e)
  ) as string[]
  const amounts = assets.map((e) => getGoodSourceAmountOfAsset(e)) as BigNumber[]

  const res = await helperContract.assetRates(usdTokenAddress, underlyings, amounts).callAsync()

  // TODO: This is reformatted to support legacy code, but can probably be avoided
  result = res.reduce((acc, item) => {
    acc[0].push(item.rate)
    acc[1].push(item.precision)
    acc[2].push(item.destAmount)
    return acc
  }, result)

  return result
}

async function getSwapToUsdRate(provider: AnyProvider, asset: Asset): Promise<BigNumber> {
  return getSwapRate(provider, asset, chainConfigs[provider.currentChainId].stablecoinForUsdRates)
}

export default { getSwapRate, getSwapToUsdRate, getSwapToUsdRateBatch, getSwapRateNew }
